import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Car, Save, Loader2, Pencil, X, ArrowRightLeft } from "lucide-react";
import api from "../../../services/api";
import ConsortiumTransferModal from "./ConsortiumTransferModal";

var STATUS_OPTIONS = [
  { value: "ativo", label: "Ativo" },
  { value: "contemplado", label: "Contemplado" },
  { value: "em_atraso", label: "Em Atraso" },
  { value: "cancelado", label: "Cancelado" },
];

var STATUS_DISPLAY = {
  ativo: { cls: "bg-emerald-100 text-emerald-700", label: "Ativo" },
  contemplado: { cls: "bg-blue-100 text-blue-700", label: "Contemplado" },
  em_atraso: { cls: "bg-red-100 text-red-700", label: "Em Atraso" },
  cancelado: { cls: "bg-gray-200 text-gray-600", label: "Cancelado" },
  transferido: { cls: "bg-gray-300 text-gray-500", label: "Transferido" },
};

function formatCurrency(v) {
  if (v == null || v === 0) return "R$ 0,00";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

export default function ConsortiumEditModal({ consortium, open, onClose, onSaved }) {
  var editingState = useState(false);
  var editing = editingState[0];
  var setEditing = editingState[1];
  var transferState = useState(false);
  var showTransfer = transferState[0];
  var setShowTransfer = transferState[1];

  var formState = useState({
    grupo: consortium?.grupo || "",
    cota: consortium?.cota || "",
    administrator: consortium?.administrator || "",
    value: consortium?.value || 0,
    installment_value: consortium?.installment_value || 0,
    installments_paid: consortium?.installments_paid || 0,
    installments_total: consortium?.installments_total || 0,
    status: consortium?.status || "ativo",
    details: consortium?.details || "",
    start_date: consortium?.start_date || "",
  });
  var form = formState[0];
  var setForm = formState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var isTransferred = consortium?.status === "transferido";

  function handleChange(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  }

  function handleNumberChange(field, value) {
    var parsed = value === "" ? 0 : parseFloat(value);
    if (isNaN(parsed)) parsed = 0;
    handleChange(field, parsed);
  }

  function handleIntChange(field, value) {
    var parsed = value === "" ? 0 : parseInt(value, 10);
    if (isNaN(parsed)) parsed = 0;
    handleChange(field, parsed);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await api.put("/consortiums/" + consortium.id, form);
      setEditing(false);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    setEditing(false);
    setError(null);
    setShowTransfer(false);
    onClose();
  }

  function handleTransferred() {
    setShowTransfer(false);
    if (onSaved) onSaved();
  }

  if (!consortium) return null;

  var statusInfo = STATUS_DISPLAY[editing ? form.status : consortium.status] || { cls: "bg-gray-100 text-gray-600", label: consortium.status };

  return (
    <>
      <Dialog open={open && !showTransfer} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0A2F3D]">
              <Car className="w-5 h-5 text-[#F26A4B]" />
              {editing ? "Editar Consorcio" : "Detalhes do Consorcio"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2" data-testid="consortium-edit-modal">
            {/* Transfer banner */}
            {isTransferred && (
              <div className="bg-gray-100 border border-gray-300 rounded-xl p-3" data-testid="consortium-transferred-banner">
                <p className="text-xs font-medium text-gray-500">Este consorcio foi transferido para:</p>
                <p className="text-sm font-semibold text-gray-700">{consortium.transferred_to_client_name || "Outro cliente"}</p>
              </div>
            )}

            {/* Received transfer banner */}
            {consortium.transferred_from_client && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="consortium-received-banner">
                <p className="text-xs font-medium text-amber-600">Transferido de:</p>
                <p className="text-sm font-semibold text-amber-800">{consortium.transferred_from_client}</p>
              </div>
            )}

            {/* Row: Grupo + Cota */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Grupo</label>
                {editing ? (
                  <input type="text" value={form.grupo} onChange={function(e) { handleChange("grupo", e.target.value); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40"
                    data-testid="consortium-edit-grupo" />
                ) : (
                  <p className={"text-sm font-medium " + (isTransferred ? "text-gray-400" : "text-[#0A2F3D]")} data-testid="consortium-detail-grupo">{consortium.grupo || "-"}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Cota</label>
                {editing ? (
                  <input type="text" value={form.cota} onChange={function(e) { handleChange("cota", e.target.value); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40"
                    data-testid="consortium-edit-cota" />
                ) : (
                  <p className={"text-sm font-medium " + (isTransferred ? "text-gray-400" : "text-[#0A2F3D]")} data-testid="consortium-detail-cota">{consortium.cota || "-"}</p>
                )}
              </div>
            </div>

            {/* Administradora */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Administradora</label>
              {editing ? (
                <input type="text" value={form.administrator} onChange={function(e) { handleChange("administrator", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40"
                  data-testid="consortium-edit-administrator" />
              ) : (
                <p className={"text-sm " + (isTransferred ? "text-gray-400" : "text-[#0A2F3D]")}>{consortium.administrator || "-"}</p>
              )}
            </div>

            {/* Row: Carta + Parcela */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Valor da Carta</label>
                {editing ? (
                  <input type="number" step="0.01" value={form.value || ""} onChange={function(e) { handleNumberChange("value", e.target.value); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40"
                    data-testid="consortium-edit-value" />
                ) : (
                  <p className={"text-sm font-semibold " + (isTransferred ? "text-gray-400" : "text-[#0A2F3D]")}>{formatCurrency(consortium.value)}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Valor da Parcela</label>
                {editing ? (
                  <input type="number" step="0.01" value={form.installment_value || ""} onChange={function(e) { handleNumberChange("installment_value", e.target.value); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40"
                    data-testid="consortium-edit-installment" />
                ) : (
                  <p className={"text-sm font-semibold " + (isTransferred ? "text-gray-400" : "text-[#0A2F3D]")}>{formatCurrency(consortium.installment_value)}</p>
                )}
              </div>
            </div>

            {/* Row: Parcelas pagas + total */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Parcelas Pagas</label>
                {editing ? (
                  <input type="number" value={form.installments_paid || ""} onChange={function(e) { handleIntChange("installments_paid", e.target.value); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40"
                    data-testid="consortium-edit-paid" />
                ) : (
                  <p className={"text-sm " + (isTransferred ? "text-gray-400" : "text-[#0A2F3D]")}>{consortium.installments_paid}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Parcelas Total</label>
                {editing ? (
                  <input type="number" value={form.installments_total || ""} onChange={function(e) { handleIntChange("installments_total", e.target.value); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40"
                    data-testid="consortium-edit-total" />
                ) : (
                  <p className={"text-sm " + (isTransferred ? "text-gray-400" : "text-[#0A2F3D]")}>{consortium.installments_total}</p>
                )}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
              {editing ? (
                <select value={form.status} onChange={function(e) { handleChange("status", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40 bg-white"
                  data-testid="consortium-edit-status">
                  {STATUS_OPTIONS.map(function(s) {
                    return <option key={s.value} value={s.value}>{s.label}</option>;
                  })}
                </select>
              ) : (
                <span className={"inline-block px-2.5 py-1 text-xs font-medium rounded-full " + statusInfo.cls}>{statusInfo.label}</span>
              )}
            </div>

            {/* Observacoes */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Observacoes</label>
              {editing ? (
                <textarea value={form.details} onChange={function(e) { handleChange("details", e.target.value); }}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F26A4B]/40 resize-none"
                  data-testid="consortium-edit-details" />
              ) : (
                <p className={"text-sm " + (isTransferred ? "text-gray-400" : "text-gray-600")}>{consortium.details || "Sem observacoes"}</p>
              )}
            </div>

            {/* Error */}
            {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              {editing ? (
                <>
                  <button onClick={function() { setEditing(false); setError(null); }}
                    className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1.5"
                    data-testid="consortium-edit-cancel">
                    <X className="w-3.5 h-3.5" />Cancelar
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="px-4 py-2 text-xs font-medium text-white bg-[#F26A4B] hover:bg-[#E05A3B] rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="consortium-edit-save">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Salvar
                  </button>
                </>
              ) : isTransferred ? (
                <p className="text-xs text-gray-400 italic">Consorcio transferido - somente leitura</p>
              ) : (
                <>
                  <button onClick={function() { setShowTransfer(true); }}
                    className="px-4 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors flex items-center gap-1.5"
                    data-testid="consortium-transfer-btn">
                    <ArrowRightLeft className="w-3.5 h-3.5" />Transferir
                  </button>
                  <button onClick={function() { setEditing(true); }}
                    className="px-4 py-2 text-xs font-medium text-white bg-[#F26A4B] hover:bg-[#E05A3B] rounded-lg transition-colors flex items-center gap-1.5"
                    data-testid="consortium-edit-btn">
                    <Pencil className="w-3.5 h-3.5" />Editar
                  </button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showTransfer && (
        <ConsortiumTransferModal
          consortium={consortium}
          open={showTransfer}
          onClose={function() { setShowTransfer(false); }}
          onTransferred={handleTransferred}
        />
      )}
    </>
  );
}
